import React, { useMemo, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { DISPLAY_STAGES } from "../shared/displayStages";
import GameStageTransition from "./GameStageTransition";
import { displayTransitionPresets } from "./displayTransitions";
import "./DisplayAnimationLab.css";

const STAGE_DEFAULT_PRESET = "stage-default";
const PREVIEW_SPEED_OPTIONS = [
  { id: "normal", label: "1x", durationScale: 1 },
  { id: "slow", label: "2x slower", durationScale: 2 },
  { id: "extra-slow", label: "4x slower", durationScale: 4 },
];
const DEFAULT_PREVIEW_SPEED_ID = PREVIEW_SPEED_OPTIONS[0].id;

const MOCK_STAGES = [
  {
    id: "active-question",
    label: "Active Question",
    displayStage: DISPLAY_STAGES.ACTIVE_QUESTION,
    category: "question",
    defaultPreset: "questionEnter",
  },
  {
    id: "show-answer",
    label: "Answer Reveal",
    displayStage: DISPLAY_STAGES.SHOW_ANSWER,
    category: "reveal",
    defaultPreset: "answerReveal",
  },
  {
    id: "fun-fact",
    label: "Fun Fact",
    displayStage: DISPLAY_STAGES.FUN_FACT,
    category: "fact",
    defaultPreset: "factFade",
  },
  {
    id: "next-question-intro",
    label: "Next Question",
    displayStage: DISPLAY_STAGES.NEXT_QUESTION_INTRO,
    category: "question",
    defaultPreset: "questionSlide",
  },
  {
    id: "leaderboard",
    label: "Leaderboard",
    displayStage: DISPLAY_STAGES.SHOW_QUESTION_RESULTS,
    category: "leaderboard",
    defaultPreset: "leaderboardReveal",
  },
  {
    id: "ad-break",
    label: "Ad Break",
    displayStage: DISPLAY_STAGES.AD_BREAK,
    category: "adBreak",
    defaultPreset: "adBreakFade",
  },
  {
    id: "round-intro",
    label: "Round Intro",
    displayStage: DISPLAY_STAGES.ROUND_INTRO,
    category: "roundIntro",
    defaultPreset: "roundIntroReveal",
  },
  {
    id: "game-over",
    label: "Game Over",
    displayStage: DISPLAY_STAGES.GAME_OVER,
    category: "completion",
    defaultPreset: "gameOverReveal",
  },
];

const ANSWERS = [
  { label: "A", text: "Lake Texoma" },
  { label: "B", text: "Possum Kingdom" },
  { label: "C", text: "Lake Ray Hubbard", correct: true },
  { label: "D", text: "Grapevine Lake" },
];

const LEADERBOARD = [
  { name: "Maya", score: 2840 },
  { name: "Jordan", score: 2690 },
  { name: "Sam", score: 2530 },
  { name: "Riley", score: 2210 },
];

function ActiveQuestionPreview() {
  return (
    <section className="animation-lab-card animation-lab-question-card">
      <p className="animation-lab-eyebrow">Question 9</p>
      <h2>Which North Texas lake is known for its urban skyline views?</h2>
      <div
        className="animation-lab-timer"
        role="progressbar"
        aria-label="Mock answer timer"
        aria-valuenow="68"
        aria-valuemin="0"
        aria-valuemax="100"
      >
        <span />
      </div>
      <div className="animation-lab-answer-grid">
        {ANSWERS.map((answer) => (
          <div className="animation-lab-answer" key={answer.label}>
            <span>{answer.label}</span>
            {answer.text}
          </div>
        ))}
      </div>
    </section>
  );
}

function AnswerRevealPreview() {
  return (
    <section className="animation-lab-card animation-lab-question-card">
      <p className="animation-lab-eyebrow">Answer Reveal</p>
      <h2>Which North Texas lake is known for its urban skyline views?</h2>
      <div className="animation-lab-answer-grid animation-lab-answer-grid--revealed">
        {ANSWERS.map((answer) => (
          <div
            className={
              answer.correct
                ? "animation-lab-answer animation-lab-answer--correct"
                : "animation-lab-answer animation-lab-answer--muted"
            }
            key={answer.label}
          >
            <span>{answer.label}</span>
            {answer.text}
          </div>
        ))}
      </div>
    </section>
  );
}

function FunFactPreview() {
  return (
    <section className="animation-lab-card animation-lab-fact-card">
      <p className="animation-lab-eyebrow">Fun Fact</p>
      <h2>Lake Ray Hubbard covers more than 22,000 acres.</h2>
      <p>
        The reservoir serves multiple cities and gives players a quick local
        trivia payoff before the next leaderboard moment.
      </p>
    </section>
  );
}

function NextQuestionIntroPreview() {
  return (
    <section className="animation-lab-card animation-lab-question-card">
      <p className="animation-lab-eyebrow">Next Question</p>
      <h2>Get ready for Question 10</h2>
      <p>
        Reset your buzzers and lock in. The next trivia challenge is almost
        here.
      </p>
    </section>
  );
}

function LeaderboardPreview() {
  return (
    <section className="animation-lab-card animation-lab-leaderboard-card">
      <div className="animation-lab-card-header">
        <p className="animation-lab-eyebrow">Parlor Standings</p>
        <h2>Top Players</h2>
      </div>
      <ol className="animation-lab-leaderboard">
        {LEADERBOARD.map((player, index) => (
          <li key={player.name}>
            <span className="animation-lab-rank">{index + 1}</span>
            <span>{player.name}</span>
            <strong>{player.score} pts</strong>
          </li>
        ))}
      </ol>
    </section>
  );
}

function AdBreakPreview() {
  return (
    <section className="animation-lab-card animation-lab-ad-card">
      <p className="animation-lab-eyebrow">Brain Battle Live</p>
      <h2>Quick Break</h2>
      <p>
        New games start around the clock. Invite a friend, refill your drink,
        and get ready for the next round.
      </p>
      <div className="animation-lab-ad-placeholder">Sponsor Placeholder</div>
      <p className="animation-lab-countdown">Next round starts in 00:18</p>
    </section>
  );
}

function RoundIntroPreview() {
  return (
    <section className="animation-lab-card animation-lab-round-intro-card">
      <p className="animation-lab-eyebrow">Next Round</p>
      <h2>Round 2</h2>
      <p>
        Get ready. The next question is coming up.
      </p>
    </section>
  );
}

function GameOverPreview() {
  return (
    <section className="animation-lab-card animation-lab-game-over-card">
      <p className="animation-lab-eyebrow">Final Results</p>
      <h2>Game Over</h2>
      <div className="animation-lab-winner">
        <span>Winner</span>
        <strong>Maya</strong>
        <em>2,840 pts</em>
      </div>
      <ol className="animation-lab-final-results">
        {LEADERBOARD.slice(0, 3).map((player, index) => (
          <li key={player.name}>
            <span className="animation-lab-rank">{index + 1}</span>
            <span>{player.name}</span>
            <strong>{player.score}</strong>
          </li>
        ))}
      </ol>
    </section>
  );
}

const STAGE_PREVIEWS = {
  "active-question": <ActiveQuestionPreview />,
  "show-answer": <AnswerRevealPreview />,
  "fun-fact": <FunFactPreview />,
  "next-question-intro": <NextQuestionIntroPreview />,
  leaderboard: <LeaderboardPreview />,
  "ad-break": <AdBreakPreview />,
  "round-intro": <RoundIntroPreview />,
  "game-over": <GameOverPreview />,
};

export default function DisplayAnimationLab() {
  const shouldReduceMotion = useReducedMotion();
  const [selectedStageId, setSelectedStageId] = useState(MOCK_STAGES[0].id);
  const [selectedPreset, setSelectedPreset] = useState(STAGE_DEFAULT_PRESET);
  const [previewSpeedId, setPreviewSpeedId] = useState(DEFAULT_PREVIEW_SPEED_ID);
  const [previewRunId, setPreviewRunId] = useState(0);

  const currentStage =
    MOCK_STAGES.find((stage) => stage.id === selectedStageId) || MOCK_STAGES[0];
  const previewSpeed =
    PREVIEW_SPEED_OPTIONS.find((speed) => speed.id === previewSpeedId) ||
    PREVIEW_SPEED_OPTIONS[0];
  const presetOptions = useMemo(
    () => [STAGE_DEFAULT_PRESET, ...Object.keys(displayTransitionPresets)],
    []
  );
  const activePreset =
    selectedPreset === STAGE_DEFAULT_PRESET
      ? currentStage.defaultPreset
      : selectedPreset;
  const selectedPresetProp =
    selectedPreset === STAGE_DEFAULT_PRESET ? currentStage.defaultPreset : selectedPreset;
  const previewTransitionKey = [
    currentStage.id,
    activePreset,
    previewSpeed.id,
    previewRunId,
  ].join("-");
  const replayPreview = () => {
    setPreviewRunId((runId) => runId + 1);
  };
  const labelMotion = shouldReduceMotion
    ? {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
      }
    : {
        initial: { opacity: 0, y: 8 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -8 },
      };

  return (
    <main className="animation-lab-shell" data-testid="display-animation-lab">
      <aside className="animation-lab-controls" aria-label="Animation lab controls">
        <div>
          <p className="animation-lab-controls__eyebrow">Display Lab</p>
          <h1>Gameplay Transitions</h1>
        </div>

        <div className="animation-lab-control-group">
          <span>Stage</span>
          <div className="animation-lab-stage-buttons">
            {MOCK_STAGES.map((stage) => (
              <button
                type="button"
                key={stage.id}
                className={
                  stage.id === currentStage.id
                    ? "animation-lab-stage-button animation-lab-stage-button--active"
                    : "animation-lab-stage-button"
                }
                onClick={() => {
                  setSelectedStageId(stage.id);
                  replayPreview();
                }}
              >
                {stage.label}
              </button>
            ))}
          </div>
        </div>

        <label className="animation-lab-control-group">
          <span>Preset</span>
          <select
            value={selectedPreset}
            onChange={(event) => {
              setSelectedPreset(event.target.value);
              replayPreview();
            }}
          >
            {presetOptions.map((presetName) => (
              <option value={presetName} key={presetName}>
                {presetName === STAGE_DEFAULT_PRESET
                  ? "Stage default"
                  : presetName}
              </option>
            ))}
          </select>
        </label>

        <div className="animation-lab-control-group">
          <span>Preview speed</span>
          <div className="animation-lab-speed-buttons">
            {PREVIEW_SPEED_OPTIONS.map((speedOption) => (
              <button
                type="button"
                key={speedOption.id}
                className={
                  speedOption.id === previewSpeed.id
                    ? "animation-lab-speed-button animation-lab-speed-button--active"
                    : "animation-lab-speed-button"
                }
                aria-pressed={speedOption.id === previewSpeed.id}
                onClick={() => {
                  setPreviewSpeedId(speedOption.id);
                  replayPreview();
                }}
              >
                {speedOption.label}
              </button>
            ))}
          </div>
        </div>

        <div className="animation-lab-action-buttons">
          <button
            type="button"
            className="animation-lab-replay-button"
            onClick={replayPreview}
          >
            Replay transition
          </button>

          <button
            type="button"
            className="animation-lab-reset-button"
            onClick={() => {
              setSelectedStageId(MOCK_STAGES[0].id);
              setSelectedPreset(STAGE_DEFAULT_PRESET);
              setPreviewSpeedId(DEFAULT_PREVIEW_SPEED_ID);
              replayPreview();
            }}
          >
            Reset
          </button>
        </div>

        <div className="animation-lab-status" aria-live="polite">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={`${previewTransitionKey}-${shouldReduceMotion}`}
              {...labelMotion}
              transition={{ duration: 0.14, ease: "easeOut" }}
            >
              <span>Current stage</span>
              <strong>{currentStage.label}</strong>
              <span>Current preset</span>
              <strong>{activePreset}</strong>
              <span>Preview speed</span>
              <strong>{previewSpeed.label}</strong>
              <span>Reduced motion</span>
              <strong>{shouldReduceMotion ? "on" : "off"}</strong>
            </motion.div>
          </AnimatePresence>
        </div>
      </aside>

      <section
        className="animation-lab-preview"
        aria-label="Animation preview"
        data-preview-key={previewTransitionKey}
        data-preview-duration-scale={String(previewSpeed.durationScale)}
      >
        <GameStageTransition
          uiState="game"
          displayStage={currentStage.displayStage}
          category={currentStage.category}
          preset={selectedPresetProp}
          durationScale={previewSpeed.durationScale}
          transitionKey={previewTransitionKey}
        >
          {STAGE_PREVIEWS[currentStage.id]}
        </GameStageTransition>
      </section>
    </main>
  );
}
